/**
 * Duration <-> sinceWhen conversions for the chief-complaints editor.
 *
 * The API stores an absolute `sinceWhen` date. The UI collects a
 * years/months/days duration and derives the date locally (or falls back to
 * the raw date if the user edits the auto-calc later).
 *
 * Kept intentionally simple — a month is 30 days, a year 365. Good enough for
 * the "how long has this been going on" UX; the server never re-derives.
 */

export function durationToSinceWhen(years, months, days, from = new Date()) {
  const y = Number(years) || 0;
  const m = Number(months) || 0;
  const d = Number(days) || 0;
  const base = new Date(from.getTime());
  base.setFullYear(base.getFullYear() - y);
  base.setMonth(base.getMonth() - m);
  base.setDate(base.getDate() - d);
  return base;
}

export function sinceWhenToDuration(sinceWhen, to = new Date()) {
  if (!sinceWhen) return { years: 0, months: 0, days: 0 };
  const start = new Date(sinceWhen);
  if (isNaN(start.getTime())) return { years: 0, months: 0, days: 0 };
  let years = to.getFullYear() - start.getFullYear();
  let months = to.getMonth() - start.getMonth();
  let days = to.getDate() - start.getDate();
  if (days < 0) {
    months -= 1;
    days += 30;
  }
  if (months < 0) {
    years -= 1;
    months += 12;
  }
  if (years < 0) years = 0;
  return { years, months: Math.max(0, months), days: Math.max(0, days) };
}

// "08 Jan 2026" — matches the "Since when" column in the Figma table.
export function formatShortDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

// "3:15 pm" — companion to formatShortDate for the audit's "Reported On"
// stack (date on top, time below). Matches the Medicines trend-modal
// timestamp cell. Empty input / invalid date returns "" so the stack
// collapses to just the date line.
export function timeOnly(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  let h = d.getHours();
  const m = d.getMinutes();
  const ampm = h >= 12 ? "pm" : "am";
  h = h % 12 || 12;
  return `${h}:${String(m).padStart(2, "0")} ${ampm}`;
}

// ISO string with UTC — matches what api-cc-v2-service expects for time.Time.
export function toIsoUtc(date) {
  return new Date(date).toISOString();
}

// UTC-midnight ISO string for the LOCAL calendar day a given Date lives on.
// Used by the Add forms when they derive sinceWhen from a duration /
// "From today" shortcut — the UI only ever collected a date, so the
// derived Date (built off `new Date()` and setMonth/setDate math) carries
// a spurious wall-clock time that leaks downstream and breaks chart
// ordering on multi-write same-day timelines. Produces the same wire
// value `toIsoUtc("YYYY-MM-DD")` does for a plain date string, so Add's
// sinceWhen matches UpdateStatus's date-picker sinceWhen byte-for-byte.
// When future UX adds an explicit datetime picker, callers should use
// `toIsoUtc` directly to preserve the user-input time.
//
// Timezone-gap clamp — IST (and any tz ahead of UTC) can report local
// date as Oct 15 while UTC is still Oct 14, so UTC-midnight of the
// local date lands slightly in the future and the CC/Dx server's
// +5-min future-check rejects it. The call sites here only ever feed
// in `now` or `now - duration` (no future datepicker), so if the
// computed UTC midnight is in the future we're safely in the
// timezone-gap case and clamp back to current UTC. Historical dates
// are unaffected. Anything beyond a day in the future (which these
// callers can't produce today) stays untouched so a future
// datepicker, if added later, still surfaces an explicit server
// error instead of silently sliding to now.
export function toIsoDateOnly(d) {
  if (!d) return "";
  const date = new Date(d);
  if (isNaN(date.getTime())) return "";
  const utcMidnight = new Date(Date.UTC(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  ));
  const nowMs = Date.now();
  const DAY_MS = 24 * 60 * 60 * 1000;
  if (utcMidnight.getTime() > nowMs && utcMidnight.getTime() - nowMs <= DAY_MS) {
    return new Date(nowMs).toISOString();
  }
  return utcMidnight.toISOString();
}
